/**
 * ProfileSettings.jsx — User settings dashboard for updating name, email, mobile, and profile picture.
 * Includes a dedicated "Logout" button at the bottom of the dashboard page.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WorkIcon from '@mui/icons-material/Work';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarRateIcon from '@mui/icons-material/StarRate';
import SpeedIcon from '@mui/icons-material/Speed';
import SettingsIcon from '@mui/icons-material/Settings';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import KYCVerification from './KYCVerification';
import ArbitratorKYC from './Arbitrator/ArbitratorKYC';
import ArbitratorAvailabilityForm from './Arbitrator/ArbitratorAvailabilityForm';
import { apiClient, clearSession } from '../utils/auth';

const CERTIFICATIONS = [
  'OSCP',
  'CISSP',
  'CEH',
  'CISM',
  'ISO27001',
  'PCI-DSS',
  'SOC2',
  'CCSP',
  'CISA',
  'CompTIA Security+',
  'AWS Solutions Architect',
  'Google Cloud Professional'
];

export default function ProfileSettings() {
  const navigate = useNavigate();
  const location = useLocation();

  // State fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [initialEmail, setInitialEmail] = useState('');
  const [accountStatus, setAccountStatus] = useState('');

  // Expandable profile states
  const [role, setRole] = useState('');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [geoPreference, setGeoPreference] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [availabilityWindow, setAvailabilityWindow] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');
  const [mode, setMode] = useState('');

  // Bidder performance metrics
  const [winRate, setWinRate] = useState('');
  const [satisfaction, setSatisfaction] = useState('');
  const [responseTime, setResponseTime] = useState('');
  const [completedDeals, setCompletedDeals] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [showKycPanel, setShowKycPanel] = useState(
    new URLSearchParams(location.search).get('verify') === 'true'
  );
  const [showAvailabilityPanel, setShowAvailabilityPanel] = useState(
    new URLSearchParams(location.search).get('availability') === 'true'
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('verify') === 'true') {
      setShowKycPanel(true);
      setShowAvailabilityPanel(false);
    } else if (params.get('availability') === 'true') {
      setShowAvailabilityPanel(true);
      setShowKycPanel(false);
    } else {
      setShowKycPanel(false);
      setShowAvailabilityPanel(false);
    }
  }, [location.search]);

  // Fetch initial profile data on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/api/v1/auth/users/me/data/');
        const normalizeText = (value) => {
          if (Array.isArray(value)) {
            return value.every((c) => typeof c === 'string' && c.length === 1) ? value.join('') : '';
          }
          return value || '';
        };
        const loadedEmail = normalizeText(data.email);
        const loadedMobile = normalizeText(data.mobile);
        setFullName(data.full_name || '');
        setEmail(loadedEmail);
        setInitialEmail(loadedEmail);
        setMobile(loadedMobile);
        setAvatarUrl(data.avatar_url || '');
        setRole(data.role || '');
        setBudgetMin(data.budget_min || '');
        setBudgetMax(data.budget_max || '');
        setGeoPreference(data.geo_preference || '');
        setCertifications(Array.isArray(data.certifications) ? data.certifications : []);
        setAvailabilityWindow(data.availability_window || '');
        setExperienceLevel(data.experience_level || '');
        setMode(data.mode || '');
        setWinRate(data.win_rate !== undefined && data.win_rate !== null ? data.win_rate : '');
        setSatisfaction(data.satisfaction !== undefined && data.satisfaction !== null ? data.satisfaction : '');
        setResponseTime(data.response_time || '');
        setCompletedDeals(data.completed_deals !== undefined && data.completed_deals !== null ? data.completed_deals : '');
        setAccountStatus(data.account_status || '');
      } catch (err) {
        setError('Failed to load profile settings. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Handle Photo upload and Base64 conversion
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Image size should be less than 2 MB.');
        return;
      }
      setError('');
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit profile settings change
  const handleSave = async (e) => {
    e.preventDefault();
    setSaveLoading(true);
    setError('');
    setMessage('');

    const certsArray = Array.isArray(certifications) ? certifications : [];

    try {
      const { data } = await apiClient.patch('/api/v1/auth/users/me/data/', {
        full_name: fullName,
        email,
        mobile,
        avatar_url: avatarUrl,
        budget_min: budgetMin || null,
        budget_max: budgetMax || null,
        geo_preference: geoPreference,
        certifications: certsArray,
        availability_window: availabilityWindow,
        experience_level: experienceLevel,
        mode: mode,
        win_rate: winRate !== '' ? parseInt(winRate, 10) : null,
        satisfaction: satisfaction !== '' ? parseInt(satisfaction, 10) : null,
        response_time: responseTime,
        completed_deals: completedDeals !== '' ? parseInt(completedDeals, 10) : null,
      });
      setMessage('Profile settings updated successfully!');
      
      // Dispatch auth change event to notify navbar/avatar immediately
      window.dispatchEvent(new Event('auth-state-change'));
      
      // If email has changed, it resets verification in backend; update local state
      if (email !== initialEmail) {
        setInitialEmail(email);
        localStorage.setItem('cmp_account_status', 'REGISTERED');
        window.dispatchEvent(new Event('auth-state-change'));
        setMessage('Profile updated! Since you changed your email address, please check your inbox to re-verify it.');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to update profile settings.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Perform logout
  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await apiClient.post('/api/v1/auth/logout/');
    } catch { /* ignore */ }
    clearSession();
    setLogoutLoading(false);
    window.dispatchEvent(new Event('auth-state-change'));
    navigate('/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', bgcolor: '#080d1a' }}>
        <CircularProgress aria-label="Loading profile" />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', mt: 6, mb: 8, px: 2 }}>
      {/* Title with rotating settings icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <SettingsIcon 
          sx={{ 
            fontSize: 32, 
            color: '#818cf8',
            animation: 'settings-spin 20s linear infinite',
            '@keyframes settings-spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }} 
        />
        <Typography variant="h4" fontWeight={700} sx={{ color: '#f8fafc', fontFamily: "'Space Grotesk', sans-serif" }}>
          Settings
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Update your personal identity details, contact information, and public platform avatar.
      </Typography>

      {/* KYC Status Card */}
      <Card
        sx={{
          mb: 4,
          background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.7) 0%, rgba(30, 41, 59, 0.7) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(129, 140, 248, 0.25)',
          borderRadius: 4,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: '#818cf8',
            boxShadow: '0 0 25px rgba(129, 140, 248, 0.2)',
          }
        }}
      >
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3.5, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="#f1f5f9" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon sx={{ color: '#818cf8', fontSize: 20 }} /> KYC Status Verification
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Required to access full marketplace features under DPDP guidelines.
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              {accountStatus === 'ACTIVE' && (
                <Chip 
                  label="Verified" 
                  color="success" 
                  size="small" 
                  sx={{ 
                    fontWeight: 700, 
                    bgcolor: 'rgba(16, 185, 129, 0.15)', 
                    color: '#34d399', 
                    border: '1px solid rgba(16, 185, 129, 0.3)' 
                  }} 
                />
              )}
              {accountStatus === 'PENDING_KYC' && (
                <Chip 
                  label="Under Verification" 
                  color="warning" 
                  size="small" 
                  sx={{ 
                    fontWeight: 700, 
                    bgcolor: 'rgba(245, 158, 11, 0.15)', 
                    color: '#fbbf24', 
                    border: '1px solid rgba(245, 158, 11, 0.3)' 
                  }} 
                />
              )}
              {accountStatus === 'REGISTERED' && (
                <Chip 
                  label="Documents Pending" 
                  color="error" 
                  size="small" 
                  sx={{ 
                    fontWeight: 700, 
                    bgcolor: 'rgba(239, 68, 68, 0.15)', 
                    color: '#f87171', 
                    border: '1px solid rgba(239, 68, 68, 0.3)' 
                  }} 
                />
              )}
            </Box>
          </Box>
          <Button
            variant="contained"
            onClick={() => setShowKycPanel(!showKycPanel)}
            id="btn-settings-kyc-redirect"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)',
              boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
              color: '#ffffff',
              borderRadius: 2.5,
              px: 3,
              py: 1,
              '&:hover': {
                background: 'linear-gradient(90deg, #4f46e5 0%, #4338ca 100%)',
                boxShadow: '0 6px 20px rgba(99, 102, 241, 0.5)',
              }
            }}
          >
            {showKycPanel ? 'Hide KYC Upload' : (accountStatus === 'ACTIVE' ? 'Update KYC Details' : 'Verify KYC Now')}
          </Button>
        </CardContent>
      </Card>

      {/* Collapsible KYC Panel */}
      <Collapse in={showKycPanel} sx={{ mb: 4 }}>
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            bgcolor: '#0d1526',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 4,
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          }}
        >
          {role === 'ARBITRATOR' ? (
            <ArbitratorKYC />
          ) : (
            <KYCVerification />
          )}
        </Paper>
      </Collapse>

      {/* Availability Settings Card (Arbitrator only) */}
      {role === 'ARBITRATOR' && (
        <Card
          sx={{
            mb: 4,
            background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.7) 0%, rgba(30, 41, 59, 0.7) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(129, 140, 248, 0.25)',
            borderRadius: 4,
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: '#818cf8',
              boxShadow: '0 0 25px rgba(129, 140, 248, 0.2)',
            }
          }}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3.5, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#f1f5f9" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ color: '#818cf8', fontSize: 20 }} /> Arbitrator Availability Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Configure your working schedule and set your online/offline status.
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => setShowAvailabilityPanel(!showAvailabilityPanel)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)',
                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)',
                color: '#ffffff',
                borderRadius: 2.5,
                px: 3,
                py: 1,
                '&:hover': {
                  background: 'linear-gradient(90deg, #4f46e5 0%, #4338ca 100%)',
                  boxShadow: '0 6px 20px rgba(99, 102, 241, 0.5)',
                }
              }}
            >
              {showAvailabilityPanel ? 'Hide Availability' : 'Manage Availability'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Collapsible Availability Panel */}
      {role === 'ARBITRATOR' && (
        <Collapse in={showAvailabilityPanel} sx={{ mb: 4 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              bgcolor: '#0d1526',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 4,
              boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
            }}
          >
            <ArbitratorAvailabilityForm />
          </Paper>
        </Collapse>
      )}

      {!showKycPanel && !showAvailabilityPanel && (
        <form onSubmit={handleSave}>
          {/* Card 1: Basic Information */}
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.4) 0%, rgba(30, 41, 59, 0.4) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.4)',
              borderRadius: 4,
              p: { xs: 3, sm: 4 },
              mb: 4,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: '#818cf8',
                boxShadow: '0 0 30px rgba(129, 140, 248, 0.25)',
                transform: 'translateY(-2px)'
              }
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ color: '#818cf8', fontSize: 22 }} /> Personal Details
            </Typography>
            <Grid container spacing={4}>
              {/* Profile Avatar section */}
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{ position: 'relative', mb: 2 }}>
                  {/* Outer glowing ring */}
                  <Box sx={{
                    position: 'absolute',
                    top: -6,
                    left: -6,
                    right: -6,
                    bottom: -6,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)',
                    zIndex: 0,
                    boxShadow: '0 0 20px rgba(99, 102, 241, 0.4)',
                    animation: 'avatar-pulse 3s infinite alternate',
                    '@keyframes avatar-pulse': {
                      '0%': { opacity: 0.6, transform: 'scale(0.98)' },
                      '100%': { opacity: 1, transform: 'scale(1.02)' },
                    }
                  }} />
                  <Avatar
                    src={avatarUrl}
                    alt={fullName || 'Avatar'}
                    sx={{
                      width: 110,
                      height: 110,
                      border: '4px solid #0d1526',
                      bgcolor: '#1e293b',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 50, color: '#94a3b8' }} />
                  </Avatar>
                  <IconButton
                    component="label"
                    sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      bgcolor: '#818cf8',
                      color: '#080d1a',
                      '&:hover': { bgcolor: '#6366f1' },
                      border: '2.5px solid #0d1526',
                      width: 32,
                      height: 32,
                      zIndex: 2,
                    }}
                    aria-label="Upload profile photo"
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                  </IconButton>
                </Box>
                <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 700 }}>
                  Profile Picture
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
                  JPG, PNG or GIF up to 2 MB. Saved securely inside your profile.
                </Typography>
              </Grid>

              {/* Form Input fields */}
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  variant="outlined"
                  fullWidth
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Email Address"
                  variant="outlined"
                  fullWidth
                  value={email}
                  disabled={true}
                  placeholder="Enter your email address"
                  InputLabelProps={{ shrink: true }}
                  helperText="Email address cannot be changed."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon sx={{ color: '#64748b', mr: 0.5 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockIcon sx={{ color: '#64748b', fontSize: 18 }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root.Mui-disabled': {
                      backgroundColor: 'rgba(255, 255, 255, 0.01)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.04)' },
                    },
                    '& .MuiFormHelperText-root': {
                      color: '#94a3b8',
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Mobile Number"
                  variant="outlined"
                  fullWidth
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  placeholder="e.g. +91 98765 43210"
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneAndroidIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Card>

          {/* Card 2: Professional matching parameters & capabilities */}
          {role && (role === 'BUYER' || role === 'BIDDER') && (
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.4) 0%, rgba(30, 41, 59, 0.4) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.4)',
                borderRadius: 4,
                p: { xs: 3, sm: 4 },
                mb: 4,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: '#818cf8',
                  boxShadow: '0 0 30px rgba(129, 140, 248, 0.25)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ color: '#818cf8', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon sx={{ color: '#818cf8', fontSize: 22 }} />
                {role === 'BUYER' ? 'Desired Bidder Match Preferences' : 'Platform Capabilities & Availability'}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                {role === 'BUYER' 
                  ? 'Define target profile requirements to search and match with suitable cybersecurity bidders.' 
                  : 'Keep your professional matching parameters updated to align with relevant client requirements.'}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    freeSolo
                    options={CERTIFICATIONS}
                    value={certifications}
                    onChange={(event, newValue) => {
                      setCertifications(newValue);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          icon={<SecurityIcon style={{ fontSize: 14, color: '#10b981' }} />}
                          label={option}
                          size="small"
                          {...getTagProps({ index })}
                          sx={{
                            color: '#ffffff',
                            borderColor: 'rgba(16, 185, 129, 0.3)',
                            bgcolor: 'rgba(16, 185, 129, 0.08)',
                            fontWeight: 600,
                            borderRadius: 2,
                            px: 1,
                            '& .MuiChip-icon': { color: 'inherit' }
                          }}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={role === 'BUYER' ? "Required Bidder Certifications" : "Your Certifications Completed"}
                        placeholder="Select or type and press Enter"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SecurityIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={role === 'BUYER' ? "Minimum Bid Budget (INR)" : "Your Minimum Rate / Budget (INR)"}
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={budgetMin}
                    onChange={(e) => setBudgetMin(e.target.value)}
                    placeholder="e.g. 100000"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MonetizationOnIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={role === 'BUYER' ? "Maximum Bid Budget (INR)" : "Your Maximum Rate / Budget (INR)"}
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={budgetMax}
                    onChange={(e) => setBudgetMax(e.target.value)}
                    placeholder="e.g. 500000"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MonetizationOnIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={role === 'BUYER' ? "Target Bidder Geographic Preference" : "Your Current Geographic Location"}
                    variant="outlined"
                    fullWidth
                    value={geoPreference}
                    onChange={(e) => setGeoPreference(e.target.value)}
                    placeholder="e.g. India, US, Global"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label={role === 'BUYER' ? "Desired Bidder Availability Window" : "Your Active Availability Window"}
                    variant="outlined"
                    fullWidth
                    value={availabilityWindow}
                    onChange={(e) => setAvailabilityWindow(e.target.value)}
                    placeholder="e.g. Immediate, Q1 2026, 30 Days"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel shrink id="exp-label" sx={{ color: '#cbd5e1' }}>
                      {role === 'BUYER' ? "Required Bidder Experience Level" : "Your Experience Level"}
                    </InputLabel>
                    <Select
                      labelId="exp-label"
                      label={role === 'BUYER' ? "Required Bidder Experience Level" : "Your Experience Level"}
                      value={experienceLevel}
                      onChange={(e) => setExperienceLevel(e.target.value)}
                      notched
                      startAdornment={
                        <InputAdornment position="start">
                          <WorkIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value=""><em>None Selected</em></MenuItem>
                      <MenuItem value="JUNIOR">Junior (0-2 Yrs)</MenuItem>
                      <MenuItem value="MID">Mid-Level (2-5 Yrs)</MenuItem>
                      <MenuItem value="SENIOR">Senior (5-8 Yrs)</MenuItem>
                      <MenuItem value="EXPERT">Expert / Lead (8+ Yrs)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel shrink id="mode-label" sx={{ color: '#cbd5e1' }}>
                      {role === 'BUYER' ? "Preferred Work Mode" : "Your Work / Service Mode"}
                    </InputLabel>
                    <Select
                      labelId="mode-label"
                      label={role === 'BUYER' ? "Preferred Work Mode" : "Your Work / Service Mode"}
                      value={mode}
                      onChange={(e) => setMode(e.target.value)}
                      notched
                      startAdornment={
                        <InputAdornment position="start">
                          <BusinessCenterIcon sx={{ color: '#818cf8', mr: 0.5 }} />
                        </InputAdornment>
                      }
                    >
                      <MenuItem value=""><em>None Selected</em></MenuItem>
                      <MenuItem value="Remote">Remote</MenuItem>
                      <MenuItem value="Onsite">Onsite</MenuItem>
                      <MenuItem value="Hybrid">Hybrid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Card>
          )}

          {/* Card 3: Performance statistics & reputation metrics */}
          {role === 'BIDDER' && (
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(13, 21, 38, 0.4) 0%, rgba(30, 41, 59, 0.4) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                boxShadow: '0 12px 40px 0 rgba(0, 0, 0, 0.4)',
                borderRadius: 4,
                p: { xs: 3, sm: 4 },
                mb: 4,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: '#10b981',
                  boxShadow: '0 0 30px rgba(16, 185, 129, 0.25)',
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ color: '#10b981', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEventsIcon sx={{ color: '#10b981', fontSize: 22 }} />
                Performance & Reputation (Anonymously Displayed)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
                Record your professional performance metrics below. These are displayed anonymously on your bidder profile to showcase your track record.
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Win Rate (%)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={winRate}
                    onChange={(e) => setWinRate(e.target.value)}
                    placeholder="e.g. 87"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmojiEventsIcon sx={{ color: '#10b981', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Client Satisfaction (%)"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={satisfaction}
                    onChange={(e) => setSatisfaction(e.target.value)}
                    placeholder="e.g. 92"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <StarRateIcon sx={{ color: '#f59e0b', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Average Response Time"
                    variant="outlined"
                    fullWidth
                    value={responseTime}
                    onChange={(e) => setResponseTime(e.target.value)}
                    placeholder="e.g. 2.5h"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SpeedIcon sx={{ color: '#a855f7', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Completed Deals"
                    variant="outlined"
                    fullWidth
                    type="number"
                    value={completedDeals}
                    onChange={(e) => setCompletedDeals(e.target.value)}
                    placeholder="e.g. 156"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessCenterIcon sx={{ color: '#3b82f6', mr: 0.5 }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Card>
          )}

          {/* Status alerts */}
          {error && (
            <Alert 
              severity="error" 
              variant="outlined" 
              sx={{ 
                mb: 3, 
                borderColor: '#ef4444', 
                color: '#fca5a5', 
                bgcolor: 'rgba(239, 68, 68, 0.05)',
                borderRadius: 2.5,
              }}
            >
              {error}
            </Alert>
          )}

          {message && (
            <Alert 
              severity="success" 
              variant="outlined" 
              sx={{ 
                mb: 3, 
                borderColor: '#22c55e', 
                color: '#86efac', 
                bgcolor: 'rgba(34, 197, 94, 0.05)',
                borderRadius: 2.5,
              }}
            >
              {message}
            </Alert>
          )}

          {/* Save changes button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            sx={{
              py: 1.8,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 16,
              background: 'linear-gradient(90deg, #6366f1 0%, #4f46e5 100%)',
              color: '#ffffff',
              boxShadow: '0 4px 20px rgba(99, 102, 241, 0.35)',
              borderRadius: 3,
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(90deg, #818cf8 0%, #6366f1 100%)',
                boxShadow: '0 6px 25px rgba(99, 102, 241, 0.55)',
                transform: 'translateY(-1px)'
              },
              '&:active': {
                transform: 'translateY(1px)'
              },
              mb: 4
            }}
          >
            {saveLoading ? 'Saving changes…' : 'Save Changes'}
          </Button>

          <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.08)' }} />

          {/* Dedicated bottom Logout button */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              Want to end your active session on this device?
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={handleLogout}
              disabled={logoutLoading}
              startIcon={logoutLoading ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon />}
              sx={{
                px: 4,
                py: 1.2,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 3,
                borderColor: '#f87171',
                color: '#f87171',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.08)',
                  borderColor: '#ef4444',
                },
              }}
            >
              {logoutLoading ? 'Logging out…' : 'Logout from Device'}
            </Button>
          </Box>
        </form>
      )}
    </Box>
  );
}
